/**
 * The UKG Pro connect contract, plus the pure logic that turns a verification
 * result into admin-facing guidance.
 *
 * Deliberately free of firebase/React imports so the mapping can be unit
 * tested — see `verification.test.ts`.
 *
 * TODO(shared): these types mirror
 * `@dadohr/shared/domainTypes/integrations/ukg` (main-v2
 * `packages/shared/src/deprecated/domainTypes/integrations/ukg.ts`), which is
 * the source of truth on the backend. They are duplicated here only because
 * that module landed in shared AFTER the version this repo installs
 * (`@dadohr/shared@1.12.0`) was published. Once shared is republished and the
 * dependency bumped, delete these declarations and import them instead — the
 * shapes are identical, so it is a pure import swap.
 */

/** Tenant service base URL is per-tenant — there is no default host to fall back to. */
export interface UkgCredentials {
  serviceUrl: string;
  username: string;
  password: string;
  /** Tenant-wide. UKG: System Configuration → Security → Web Services. */
  customerApiKey: string;
  /** Per service account. UKG: the Service Account admin page. SOAP-only. */
  userApiKey: string;
}

/** Outcome of probing one UKG transport. `error` is the raw provider message. */
export interface UkgTransportCheck {
  ok: boolean;
  error?: string;
}

/**
 * Both transports are always reported independently: REST (reads) authenticates
 * with username + password + Customer API key, SOAP (write-back) additionally
 * needs the User API key. Collapsing them into one pass/fail would hide the most
 * common misconfiguration — a wrong User API key, which REST never exercises.
 */
export interface UkgVerificationResult {
  rest: UkgTransportCheck;
  soap: UkgTransportCheck;
  ok: boolean;
}

export interface UkgConnectionStatus {
  connected: boolean;
  active: boolean;
  serviceUrl: string | null;
}

export enum UkgConnectErrorCode {
  INVALID_REQUEST = 'UKG_ERR_2001',
  NOT_CONFIGURED = 'UKG_ERR_4001',
  REST_AUTH_FAILED = 'UKG_ERR_5001',
  SOAP_AUTH_FAILED = 'UKG_ERR_5002'
}

/** Which of the four rest/soap combinations we are looking at. */
export enum UkgGuidance {
  /** Reads and writes both work. */
  CONNECTED = 'CONNECTED',
  /** Neither transport authenticated — the shared credentials are wrong. */
  BOTH_FAILED = 'BOTH_FAILED',
  /** Reads work, writes don't: the User API key is the only credential REST skips. */
  WRITES_FAILED = 'WRITES_FAILED',
  /** Writes work but reads don't — not a state UKG's credential model explains. */
  READS_FAILED = 'READS_FAILED'
}

export const toUkgGuidance = (result: UkgVerificationResult): UkgGuidance => {
  const rest = !!result.rest?.ok;
  const soap = !!result.soap?.ok;
  if (rest && soap) {
    return UkgGuidance.CONNECTED;
  }
  if (!rest && !soap) {
    return UkgGuidance.BOTH_FAILED;
  }
  return rest ? UkgGuidance.WRITES_FAILED : UkgGuidance.READS_FAILED;
};

/**
 * A failed UKG callable. `verification` is present whenever the backend told us
 * which transport failed — that is far more actionable than the message, so the
 * UI prefers it and falls back to `code`/`message` only when it is missing.
 */
export interface UkgCallFailure {
  /** Typed `UKG_ERR_*` code from the HttpsError details, when present. */
  code: string | null;
  verification: UkgVerificationResult | null;
  message: string;
}

const isTransportCheck = (v: unknown): v is UkgTransportCheck =>
  !!v && typeof v === 'object' && typeof (v as UkgTransportCheck).ok === 'boolean';

/**
 * Pulls the typed payload out of a thrown HttpsError.
 *
 * The callable SDK puts our `details` object on the error and uses `error.code`
 * for the transport-level code (`invalid-argument`, `not-found`, …), so the
 * `UKG_ERR_*` code we care about is `details.code`, not `error.code`.
 */
export const parseUkgError = (err: unknown): UkgCallFailure => {
  const details = (err as { details?: unknown } | undefined)?.details as
    | { code?: unknown; rest?: unknown; soap?: unknown }
    | undefined;

  const rest = isTransportCheck(details?.rest) ? details?.rest : null;
  const soap = isTransportCheck(details?.soap) ? details?.soap : null;

  return {
    code: typeof details?.code === 'string' ? details.code : null,
    // Only a complete pair is a verification result; a half-populated payload
    // would render a transport line we never actually got an answer for.
    verification:
      rest && soap ? { rest, soap, ok: rest.ok && soap.ok } : null,
    message:
      (err as { message?: unknown } | undefined)?.message &&
      typeof (err as { message?: unknown }).message === 'string'
        ? ((err as { message: string }).message as string)
        : ''
  };
};

/**
 * Every submit must carry all five real values: the backend verifies the whole
 * credential set against UKG *before* storing it, so there is nothing to merge
 * a masked placeholder into — a `●●●●` would simply fail verification.
 */
export const isUkgCredentialsComplete = (creds: UkgCredentials): boolean =>
  ([
    creds.serviceUrl,
    creds.username,
    creds.password,
    creds.customerApiKey,
    creds.userApiKey
  ] as string[]).every((v) => !!v && v.trim().length > 0);

/**
 * Normalises a filled-in form into the callable payload. Copy-pasting from the
 * UKG admin pages routinely picks up surrounding whitespace, so the URL, the
 * username and the two keys are trimmed. The password is sent exactly as typed
 * — trimming it could silently change a legitimate credential.
 */
export const toUkgCredentialsPayload = (creds: UkgCredentials): UkgCredentials => ({
  serviceUrl: creds.serviceUrl.trim(),
  username: creds.username.trim(),
  password: creds.password,
  customerApiKey: creds.customerApiKey.trim(),
  userApiKey: creds.userApiKey.trim()
});
